/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import { API, Auth } from 'aws-amplify';
import { isEmpty } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import {
  userExist,
  customers,
  getOrgDetailsByUserName,
  serviceCategoryV3,
  masterData,
  masterDataCategory,
} from '../../graphql/queries';
import { serviceCategoryMock } from '../../Modules/Jobs/Components/IssuesRaised/MockData';
import { Code } from '../../API';
import { AuthHeader, AWSAuthMode, ID_TOKEN_SYSTEM_CLAIMS, MasterData } from '../Constants/App';
import { getWebInfo, shareRecentCall, signedFetch, sortByLegalName } from '../Utilities/utils';
import {
  CreateServiceDto,
  CreateTaskSubtaskMappingDto,
  IGraphQLResponse,
  ILoginCustomer,
  ServiceTaskMapping,
} from '../Models';
import { AuthenticationService } from './AuthenticationService';
import awsConfig from '../../Configuration/environment-config';

export default class MasterDataApiService {
  static async getCurrentUserInfo(): Promise<any> {
    try {
      const { 'cognito:username': username, ...claims } = (await Auth.currentSession()).getIdToken().decodePayload();
      const attributes = Object.fromEntries(
        Object.entries(claims).filter(([claim]) => !ID_TOKEN_SYSTEM_CLAIMS.has(claim))
      );
      return { username: username ?? '', attributes };
    } catch (error) {
      console.error(`CurrentUserInfo: Error occurred while fetching user info ${JSON.stringify(error)}`);
    }
    return null;
  }

  /**
   * Organizations whose vendor users act on behalf of several customers (Velociti, UNIFI).
   * Driven by master data so a new organization can be onboarded without a release.
   */
  static async getMultiCustomerOrgIds(): Promise<string[]> {
    const responses = await Promise.all([
      MasterDataApiService.getMasterDataCategories(MasterData.VELOCITI_ORG_ID),
      MasterDataApiService.getMasterDataCategories(MasterData.UNIFI_ORG_ID),
    ]);
    return responses
      .flatMap(({ data }) => data ?? [])
      .map((item: { value: string }) => item?.value)
      .filter(Boolean);
  }

  static async checkIfUserExists(userId: string) {
    try {
      const response: IGraphQLResponse = await API.graphql(
        {
          query: userExist,
          variables: { user: userId },
          authMode: AWSAuthMode.API_KEY,
        },
        { ...getWebInfo() }
      );
      return { data: response.data?.userExist ?? false, errors: [] };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async getAllServiceCategoryV2(isMocked = false) {
    if (!isMocked) {
      try {
        const response: IGraphQLResponse = await API.graphql(
          {
            query: serviceCategoryV3,
            variables: { limit: 500, pageNumber: 1 },
            authMode: AWSAuthMode.API_KEY,
          },
          { ...getWebInfo() }
        );
        return { data: response.data?.serviceCategoryV3?.data ?? [], errors: [] };
      } catch (e: any) {
        return { data: [], errors: e.errors };
      }
    }
    return { data: serviceCategoryMock, errors: [] };
  }

  static async customerInfo() {
    try {
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
      const response: IGraphQLResponse = await API.graphql(
        {
          query: customers,
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );
      return { data: response.data?.customers?.data ?? [], errors: [] };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  // Shared with any identical call made in the last few seconds, so login only fetches it once.
  static getOrgDetailsByUserName() {
    return shareRecentCall('getOrgDetailsByUserName', () => MasterDataApiService.fetchOrgDetailsByUserName());
  }

  private static async fetchOrgDetailsByUserName() {
    try {
      const authHeaders: AuthHeader = await AuthenticationService.getAuthHeaders();
      const getOrgDetailsByUserNameResponse: IGraphQLResponse = await API.graphql(
        {
          query: getOrgDetailsByUserName,
          variables: {},
          authMode: AWSAuthMode.COGNITO,
        },
        { ...getWebInfo(), ...authHeaders }
      );
      return { data: getOrgDetailsByUserNameResponse?.data?.getOrgDetailsByUserName, error: [] };
    } catch (err) {
      return { data: [], error: err };
    }
  }

  static async getAllServiceCategoryV3(
    orgId?: string,
    limit?: number,
    pageNumber?: number,
    taskId?: string,
    serviceCategoryId?: string,
    isMocked = false
  ) {
    const variables: {
      limit?: number;
      orgId?: string;
      pageNumber?: number;
      serviceCategoryId?: string;
      taskId?: string;
    } = {};
    if (limit) variables.limit = limit;
    if (pageNumber) variables.pageNumber = pageNumber;
    if (orgId) variables.orgId = orgId;
    if (taskId) variables.taskId = taskId;
    if (serviceCategoryId) variables.serviceCategoryId = serviceCategoryId;

    if (!isMocked) {
      try {
        let response: IGraphQLResponse = await API.graphql(
          {
            query: serviceCategoryV3,
            variables,
            authMode: AWSAuthMode.API_KEY,
          },
          { ...getWebInfo() }
        );
        if (!isEmpty(orgId) && isEmpty(response.data?.serviceCategoryV3.data)) {
          const variablesWithoutOrgId = { ...variables };
          delete variablesWithoutOrgId.orgId; // If customer/vendor tries to get services for the 1st time.

          response = await API.graphql(
            {
              query: serviceCategoryV3,
              variables: { ...variablesWithoutOrgId },
              authMode: AWSAuthMode.API_KEY,
            },
            { ...getWebInfo() }
          );

          response = await API.graphql(
            {
              query: serviceCategoryV3,
              variables: { ...variablesWithoutOrgId, limit: response.data?.serviceCategoryV3?.metadata.totalCount },
              authMode: AWSAuthMode.API_KEY,
            },
            { ...getWebInfo() }
          );
        } else {
          response = await API.graphql(
            {
              query: serviceCategoryV3,
              variables: { ...variables, limit: response.data?.serviceCategoryV3?.metadata.totalCount },
              authMode: AWSAuthMode.API_KEY,
            },
            { ...getWebInfo() }
          );
        }
        return { data: response.data?.serviceCategoryV3.data ?? [], errors: [] };
      } catch (e: any) {
        return { data: [], errors: e.errors };
      }
    }
    return { data: [], errors: [] };
  }

  static async getUserScopes(userId: string) {
    try {
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'getUserScopes');
      if (!endpointConfig?.endpoint) {
        return { data: [], error: [{ message: 'Endpoint not found' }] };
      }
      const url = new URL(endpointConfig.endpoint);
      url.searchParams.set('userId', userId);

      const response = await signedFetch({
        method: 'GET',
        url: url.toString(),
        requestId: uuidv4(),
        userId,
      });
      return { data: response?.data ?? [], error: response?.errors ?? [] };
    } catch (err) {
      return { data: [], error: err };
    }
  }

  static async getLoginRolesAndCustomers(
    userId: string,
    orgId?: string
  ): Promise<{ userScopesResponse: any; customersList: ILoginCustomer[] | null }> {
    const userScopesEndpoint = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'getUserScopes')?.endpoint;
    if (userScopesEndpoint && userId) {
      const rolesUrl = new URL(`${userScopesEndpoint}/v2`);
      rolesUrl.searchParams.set('userId', userId);
      const customersUrl = `${userScopesEndpoint}/user/${encodeURIComponent(userId)}/customers`;
      const [rolesResponse, customersResponse] = await Promise.all([
        signedFetch({ method: 'GET', url: rolesUrl.toString(), requestId: uuidv4(), userId }),
        signedFetch({
          method: 'GET',
          url: customersUrl,
          requestId: uuidv4(),
          userId,
          ...(orgId ? { abnHeaders: { orgid: orgId } } : {}),
        }),
      ]);
      if (
        !rolesResponse.errors.length &&
        !customersResponse.errors.length &&
        Array.isArray(rolesResponse.data) &&
        Array.isArray(customersResponse.data)
      ) {
        return {
          userScopesResponse: { data: rolesResponse.data, error: [] },
          customersList: sortByLegalName(customersResponse.data),
        };
      }
    }
    return { userScopesResponse: await MasterDataApiService.getUserScopes(userId), customersList: null };
  }

  static async getMasterDataCategories(id: string, keyId?: string) {
    const variables: { id: string; keyId?: string } = { id };
    if (!isEmpty(keyId)) {
      variables.keyId = keyId;
    }
    try {
      const response: IGraphQLResponse = await API.graphql(
        {
          query: masterData,
          variables,
          authMode: AWSAuthMode.API_KEY,
        },
        { ...getWebInfo() }
      );
      return {
        data: response.data?.masterData.data ?? [],
        metaData: response.data?.masterData.metadata ?? [],
        errors: [],
      };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async getAllFacilityTypes(limit?: number, pageNumber?: number) {
    try {
      const variables: {
        id: string;
        limit?: number;
        pageNumber?: number;
      } = {
        id: 'FACILITY_TYPE',
      };
      if (limit) {
        variables.limit = limit;
        if (pageNumber) variables.pageNumber = pageNumber;
      }
      const response: IGraphQLResponse = await API.graphql(
        {
          query: masterData,
          variables,
          authMode: AWSAuthMode.API_KEY,
        },
        { ...getWebInfo() }
      );
      return {
        data: response.data?.masterData?.data ?? [],
        metaData: response.data?.masterData?.metadata ?? [],
        errors: [],
      };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async getFacilityTypes(code: Code, keyCode: string) {
    try {
      const response: IGraphQLResponse = await API.graphql(
        {
          query: masterDataCategory,
          variables: { code, keyCode },
          authMode: AWSAuthMode.API_KEY,
        },
        { ...getWebInfo() }
      );
      return { data: response.data?.masterDataCategory ?? [], errors: [] };
    } catch (e: any) {
      return { data: [], errors: e.errors };
    }
  }

  static async getAllPricingOptions() {
    try {
      const response: IGraphQLResponse = await API.graphql(
        {
          query: masterData,
          variables: { id: 'PRICING_OPTION' },
          authMode: AWSAuthMode.API_KEY,
        },
        { ...getWebInfo() }
      );
      return {
        data: response.data?.masterData.data ?? [],
        metaData: response.data?.masterData.metadata ?? {},
        errors: [],
      };
    } catch (e: any) {
      return { data: [], errors: e.errors, metaData: {} };
    }
  }

  static async getServiceCategory(orgId: string, flowType: string, limit?: number) {
    try {
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'serviceCategory');

      if (!endpointConfig?.endpoint) {
        console.error('Endpoint configuration not found');
        return { success: false, errors: [{ message: 'Endpoint not found' }] };
      }

      const url = new URL(endpointConfig.endpoint);
      url.searchParams.append('orgId', orgId);
      url.searchParams.append('flowType', flowType);
      if (typeof limit === 'number') {
        url.searchParams.append('limit', limit.toString());
      }
      const response = await signedFetch({
        method: 'GET',
        url: url.toString(),
      });
      return {
        data: response?.data?.data ?? [],
        errors: response?.errors ?? [],
        status: response?.data?.status ?? null,
      };
    } catch (e: any) {
      console.error(`failed to fetch service categories for the orgId - ${orgId}`, e);
    }
  }

  static async getAllTasks(userId: string, page: number, limit: number, taskName?: string) {
    try {
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'getAllTasks');

      if (!endpointConfig?.endpoint) {
        console.error('Endpoint configuration not found');
        return { success: false, errors: [{ message: 'Endpoint not found' }] };
      }

      const url = new URL(endpointConfig.endpoint);
      url.searchParams.append('pageNumber', page?.toString());
      url.searchParams.append('limit', limit.toString());
      if (!isEmpty(taskName)) {
        url.searchParams.append('taskName', String(taskName));
      }

      const response = await signedFetch({
        method: 'GET',
        url: url.toString(),
        requestId: uuidv4(),
        userId,
      });

      return {
        data: response?.data?.data ?? [],
        errors: response?.errors ?? [],
        metaData: {
          limit: response?.data?.limit,
          total: response?.data?.total,
          page: response?.data?.page,
          totalPages: response?.data?.totalPages,
        },
      };
    } catch (e: any) {
      console.error(`failed to fetch all tasks`, e);
    }
  }

  static async createTask(
    userId: string,
    taskName: string,
    taskDescription: string,
    rate: number,
    unit: string,
    taskImageUrl?: string,
    ISSACode?: string
  ) {
    try {
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'createTask');

      if (!endpointConfig?.endpoint) {
        console.error('Endpoint configuration not found');
        return {
          success: false,
          errors: [{ message: 'Endpoint not found' }],
        };
      }

      const payload = [
        {
          taskName,
          taskDescription,
          taskImageUrl: taskImageUrl ?? '',
          ISSACode: ISSACode ?? '',
          rate,
          unit,
          taskId: uuidv4(),
        },
      ];

      const response = await signedFetch({
        method: 'POST',
        url: endpointConfig.endpoint,
        requestId: uuidv4(),
        userId,
        body: payload,
      });

      return {
        data: response?.data ?? [],
        errors: response?.errors ?? [],
      };
    } catch (e: any) {
      console.error('failed to create task', e);
    }
  }

  static async serviceTaskMapping(userId: string, serviceTaskMapping: ServiceTaskMapping) {
    try {
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'serviceTaskMapping');

      if (!endpointConfig?.endpoint) {
        return {
          success: false,
          errors: [{ message: 'Endpoint not found' }],
        };
      }

      const response = await signedFetch({
        method: 'POST',
        url: endpointConfig.endpoint,
        requestId: uuidv4(),
        userId,
        body: serviceTaskMapping,
      });

      return {
        data: response?.data ?? [],
        errors: response?.errors ?? [],
        status: response?.status ?? null,
      };
    } catch (e: any) {
      console.error('failed to create task', e);
    }
  }

  static async createService(userId: string, createServicePayload: CreateServiceDto[]) {
    try {
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'createService');

      if (!endpointConfig?.endpoint) {
        return {
          success: false,
          errors: [{ message: 'Endpoint not found' }],
        };
      }

      const response = await signedFetch({
        method: 'POST',
        url: endpointConfig.endpoint,
        requestId: uuidv4(),
        userId,
        body: createServicePayload,
      });

      return {
        data: response?.data ?? [],
        errors: response?.errors ?? [],
      };
    } catch (e: any) {
      console.error('failed to create service', e);
    }
  }

  static async taskSubTaskMapping(userId: string, payload: CreateTaskSubtaskMappingDto) {
    try {
      const endpointConfig = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'taskSubTaskMapping');

      if (!endpointConfig?.endpoint) {
        return {
          success: false,
          errors: [{ message: 'Endpoint not found' }],
        };
      }

      const response = await signedFetch({
        method: 'POST',
        url: endpointConfig.endpoint,
        requestId: uuidv4(),
        userId,
        body: payload,
      });

      return {
        data: response?.data ?? [],
        errors: response?.errors ?? [],
      };
    } catch (e) {
      console.error('failed to create task', e);
    }
  }
}
